import { supabase } from "@/lib";
import { fileUtils } from "@/lib/utils/fileUtils.js";
import PropTypes from "prop-types";

/**
 * Create a new token usage record
 * @param {Object} params Token usage parameters
 * @returns {Promise<Object>} Created token usage record
 */
export async function createTokenUsage({
  userId,
  workspaceId,
  requestId,
  promptTokens,
  completionTokens,
  totalTokens,
  cachedTokens = 0,
  audioTokensPrompt = 0,
  reasoningTokens = 0,
  audioTokensCompletion = 0,
  acceptedPredictionTokens = 0,
  rejectedPredictionTokens = 0,
  modelId,
  featureId,
  metadata = {},
}) {
  try {
    // Validate workspace_id exists if provided
    let validWorkspaceId = workspaceId;
    if (workspaceId) {
      const { data: workspace, error: workspaceError } = await supabase
        .from("workspaces")
        .select("id")
        .eq("id", workspaceId)
        .single();

      // If workspace doesn't exist or there's an error, set to null
      if (workspaceError || !workspace) {
        console.warn(
          `Workspace ${workspaceId} not found, setting workspace_id to null for token usage record`
        );
        validWorkspaceId = null;
      }
    }

    const { data, error } = await supabase
      .from("user_token_stats")
      .insert({
        user_id: userId,
        workspace_id: validWorkspaceId,
        request_id: requestId || fileUtils.generateUUID(),
        prompt_tokens: promptTokens,
        completion_tokens: completionTokens,
        total_tokens: totalTokens,
        cached_tokens: cachedTokens,
        audio_tokens_prompt: audioTokensPrompt,
        reasoning_tokens: reasoningTokens,
        audio_tokens_completion: audioTokensCompletion,
        accepted_prediction_tokens: acceptedPredictionTokens,
        rejected_prediction_tokens: rejectedPredictionTokens,
        model_id: modelId,
        feature_id: featureId,
        metadata,
        created_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error creating token usage record:", error);
    throw error;
  }
}

createTokenUsage.propTypes = {
  userId: PropTypes.string.isRequired,
  workspaceId: PropTypes.string,
  requestId: PropTypes.string,
  promptTokens: PropTypes.number.isRequired,
  completionTokens: PropTypes.number.isRequired,
  totalTokens: PropTypes.number.isRequired,
  cachedTokens: PropTypes.number,
  audioTokensPrompt: PropTypes.number,
  reasoningTokens: PropTypes.number,
  audioTokensCompletion: PropTypes.number,
  acceptedPredictionTokens: PropTypes.number,
  rejectedPredictionTokens: PropTypes.number,
  modelId: PropTypes.string,
  featureId: PropTypes.string,
  metadata: PropTypes.object,
};

/**
 * Get token usage by ID
 * @param {string} id Token usage record ID
 * @returns {Promise<Object>} Token usage record
 */
export async function getTokenUsage(id) {
  try {
    const { data, error } = await supabase
      .from("user_token_stats")
      .select("*")
      .eq("id", id)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error fetching token usage:", error);
    throw error;
  }
}

getTokenUsage.propTypes = {
  id: PropTypes.string.isRequired,
};

/**
 * Get token usage for a user
 * @param {string} userId User ID
 * @param {Object} options Query options
 * @returns {Promise<Array>} Token usage records
 */
export async function getTokenUsageByUser(
  userId,
  { limit = 50, offset = 0, startDate, endDate, featureId, modelId } = {}
) {
  try {
    let query = supabase
      .from("user_token_stats")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .range(offset, offset + limit - 1);

    if (startDate) {
      query = query.gte("created_at", startDate);
    }

    if (endDate) {
      query = query.lte("created_at", endDate);
    }

    if (featureId) {
      query = query.eq("feature_id", featureId);
    }

    if (modelId) {
      query = query.eq("model_id", modelId);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error fetching token usage by user:", error);
    throw error;
  }
}

getTokenUsageByUser.propTypes = {
  userId: PropTypes.string.isRequired,
  options: PropTypes.shape({
    limit: PropTypes.number,
    offset: PropTypes.number,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    featureId: PropTypes.string,
    modelId: PropTypes.string,
  }),
};

/**
 * Get token usage for a workspace
 * @param {string} workspaceId Workspace ID
 * @param {Object} options Query options
 * @returns {Promise<Array>} Token usage records
 */
export async function getTokenUsageByWorkspace(
  workspaceId,
  {
    limit = 50,
    offset = 0,
    startDate,
    endDate,
    featureId,
    modelId,
    userId,
  } = {}
) {
  try {
    let query = supabase
      .from("user_token_stats")
      .select("*")
      .eq("workspace_id", workspaceId)
      .order("created_at", { ascending: false })
      .range(offset, offset + limit - 1);

    if (startDate) {
      query = query.gte("created_at", startDate);
    }

    if (endDate) {
      query = query.lte("created_at", endDate);
    }

    if (featureId) {
      query = query.eq("feature_id", featureId);
    }

    if (modelId) {
      query = query.eq("model_id", modelId);
    }

    if (userId) {
      query = query.eq("user_id", userId);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error fetching token usage by workspace:", error);
    throw error;
  }
}

getTokenUsageByWorkspace.propTypes = {
  workspaceId: PropTypes.string.isRequired,
  options: PropTypes.shape({
    limit: PropTypes.number,
    offset: PropTypes.number,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    featureId: PropTypes.string,
    modelId: PropTypes.string,
    userId: PropTypes.string,
  }),
};

/**
 * Get token usage summary for a user
 * @param {string} userId User ID
 * @param {Object} options Query options
 * @returns {Promise<Object>} Token usage summary
 */
export async function getUserTokenUsageSummary(
  userId,
  { startDate, endDate, featureId, modelId } = {}
) {
  try {
    const records = await getTokenUsageByUser(userId, {
      limit: 1000,
      startDate,
      endDate,
      featureId,
      modelId,
    });

    return calculateTokenSummary(records);
  } catch (error) {
    console.error("Error getting user token usage summary:", error);
    throw error;
  }
}

getUserTokenUsageSummary.propTypes = {
  userId: PropTypes.string.isRequired,
  options: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    featureId: PropTypes.string,
    modelId: PropTypes.string,
  }),
};

/**
 * Get token usage summary for a workspace
 * @param {string} workspaceId Workspace ID
 * @param {Object} options Query options
 * @returns {Promise<Object>} Token usage summary
 */
export async function getWorkspaceTokenUsageSummary(
  workspaceId,
  { startDate, endDate, featureId, modelId, userId } = {}
) {
  try {
    const records = await getTokenUsageByWorkspace(workspaceId, {
      limit: 1000,
      startDate,
      endDate,
      featureId,
      modelId,
      userId,
    });

    return calculateTokenSummary(records);
  } catch (error) {
    console.error("Error getting workspace token usage summary:", error);
    throw error;
  }
}

getWorkspaceTokenUsageSummary.propTypes = {
  workspaceId: PropTypes.string.isRequired,
  options: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    featureId: PropTypes.string,
    modelId: PropTypes.string,
    userId: PropTypes.string,
  }),
};

/**
 * Helper function to calculate token usage summary from records
 * @param {Array} records Token usage records
 * @returns {Object} Token usage summary
 */
function calculateTokenSummary(records) {
  return records.reduce(
    (summary, record) => {
      summary.promptTokens += record.prompt_tokens || 0;
      summary.completionTokens += record.completion_tokens || 0;
      summary.totalTokens += record.total_tokens || 0;
      summary.cachedTokens += record.cached_tokens || 0;
      summary.audioTokensPrompt += record.audio_tokens_prompt || 0;
      summary.reasoningTokens += record.reasoning_tokens || 0;
      summary.audioTokensCompletion += record.audio_tokens_completion || 0;
      summary.acceptedPredictionTokens +=
        record.accepted_prediction_tokens || 0;
      summary.rejectedPredictionTokens +=
        record.rejected_prediction_tokens || 0;

      if (record.feature_id && !summary.featureBreakdown[record.feature_id]) {
        summary.featureBreakdown[record.feature_id] = 0;
      }
      if (record.feature_id) {
        summary.featureBreakdown[record.feature_id] += record.total_tokens || 0;
      }

      if (record.model_id && !summary.modelBreakdown[record.model_id]) {
        summary.modelBreakdown[record.model_id] = 0;
      }
      if (record.model_id) {
        summary.modelBreakdown[record.model_id] += record.total_tokens || 0;
      }

      return summary;
    },
    {
      promptTokens: 0,
      completionTokens: 0,
      totalTokens: 0,
      cachedTokens: 0,
      audioTokensPrompt: 0,
      reasoningTokens: 0,
      audioTokensCompletion: 0,
      acceptedPredictionTokens: 0,
      rejectedPredictionTokens: 0,
      featureBreakdown: {},
      modelBreakdown: {},
    }
  );
}

/**
 * Record token usage from OpenAI response
 * @param {Object} params Recording parameters
 * @returns {Promise<Object>} Created token usage record
 */
export async function recordOpenAITokenUsage({
  userId,
  workspaceId,
  requestId,
  usageData,
  modelId,
  featureId,
  metadata = {},
}) {
  try {
    // Extract token information from OpenAI format
    const promptTokens = usageData.prompt_tokens || 0;
    const completionTokens = usageData.completion_tokens || 0;
    const totalTokens = usageData.total_tokens || 0;

    const promptDetails = usageData.prompt_tokens_details || {};
    const completionDetails = usageData.completion_tokens_details || {};

    return createTokenUsage({
      userId,
      workspaceId,
      requestId,
      promptTokens,
      completionTokens,
      totalTokens,
      cachedTokens: promptDetails.cached_tokens || 0,
      audioTokensPrompt: promptDetails.audio_tokens || 0,
      reasoningTokens: completionDetails.reasoning_tokens || 0,
      audioTokensCompletion: completionDetails.audio_tokens || 0,
      acceptedPredictionTokens:
        completionDetails.accepted_prediction_tokens || 0,
      rejectedPredictionTokens:
        completionDetails.rejected_prediction_tokens || 0,
      modelId,
      featureId,
      metadata,
    });
  } catch (error) {
    console.error("Error recording OpenAI token usage:", error);
    throw error;
  }
}

recordOpenAITokenUsage.propTypes = {
  userId: PropTypes.string.isRequired,
  workspaceId: PropTypes.string,
  requestId: PropTypes.string,
  usageData: PropTypes.shape({
    prompt_tokens: PropTypes.number,
    completion_tokens: PropTypes.number,
    total_tokens: PropTypes.number,
    prompt_tokens_details: PropTypes.shape({
      cached_tokens: PropTypes.number,
      audio_tokens: PropTypes.number,
    }),
    completion_tokens_details: PropTypes.shape({
      reasoning_tokens: PropTypes.number,
      audio_tokens: PropTypes.number,
      accepted_prediction_tokens: PropTypes.number,
      rejected_prediction_tokens: PropTypes.number,
    }),
  }).isRequired,
  modelId: PropTypes.string,
  featureId: PropTypes.string,
  metadata: PropTypes.object,
};

// Named export for all functions
export const userTokenStats = {
  create: createTokenUsage,
  get: getTokenUsage,
  getByUser: getTokenUsageByUser,
  getByWorkspace: getTokenUsageByWorkspace,
  getUserSummary: getUserTokenUsageSummary,
  getWorkspaceSummary: getWorkspaceTokenUsageSummary,
  recordOpenAIUsage: recordOpenAITokenUsage,
};

// Default export
export default userTokenStats;
